import { FormGroupRules } from '@shared/models/interfaces/form-group-rules.model';
import { FormGroup } from '@angular/forms';
import { addRemoveError, getFormArray } from '@shared/helpers/form.helper';
import { toNum } from '@shared/helpers/math.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { isNilOrEmpty, toBoolean } from '@shared/helpers/utils.helper';
import { datiBilancioCkHelper, getDatiBilancioControl, getDatiBilancioGroup } from './helpers/dati-bilancio-form.helpers';
import { isEqual } from 'lodash-es';
import { formPaths } from '@shared/constants/form-paths.const';
import { MyElContrattoSocietaCertificazione } from '../../../models/interfaces/revisione.interface';

export const datiBilancioCertificazioneRules: FormGroupRules = {
    my_CkEnteTenutoCertBil: (form: FormGroup) => {
        const my_CkEnteTenutoCertBilControl = getDatiBilancioControl(form, 'my_CkEnteTenutoCertBil');
        const my_TotConfSociFin = toNum(getDatiBilancioControl(form, 'my_TotConfSociFin')?.value);
        const showError = my_CkEnteTenutoCertBilControl?.value === '0' && my_TotConfSociFin > 2_000_000;
        const errors = my_CkEnteTenutoCertBilControl.errors ? my_CkEnteTenutoCertBilControl.errors : {};
        addRemoveError(showError, errors, 'my_CkEnteTenutoCertBil', {
            message: 'La cooperativa è tenuta alla certificazione di bilancio',
            status: 'La cooperativa è tenuta alla certificazione di bilancio, il totale conferito dai soci finanziatori è maggiore di € 2.000.000',
        });

        addRemoveError(isNilOrEmpty(my_CkEnteTenutoCertBilControl.value), errors, 'my_CkEnteTenutoCertBil', {
            status: REQUIRED_STATUS,
        });

        my_CkEnteTenutoCertBilControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_CkCtrlCertifPartecSpA: (form: FormGroup) => {
        my_CkCtrlCertiHelper(form, 'my_CkCtrlCertifPartecSpA');
    },

    my_CkCtrlCertifPrestitiEConferim: (form: FormGroup) => {
        my_CkCtrlCertiHelper(form, 'my_CkCtrlCertifPrestitiEConferim');
    },

    my_CkCtrlCertifRiserveIndivis: (form: FormGroup) => {
        my_CkCtrlCertiHelper(form, 'my_CkCtrlCertifRiserveIndivis');
    },

    my_CkCtrlCertifValoreProd: (form: FormGroup) => {
        my_CkCtrlCertiHelper(form, 'my_CkCtrlCertifValoreProd');
    },

    my_CkProvvedeCertBil: (form: FormGroup) => {
        datiBilancioCkHelper(form, 'my_CkProvvedeCertBil', 'my_CkEnteTenutoCertBil', 1);
    },

    my_RagSocRevIncaricataChoice: (form: FormGroup) => {
        const control = getDatiBilancioControl(form, 'my_RagSocRevIncaricataChoice');
        const my_CkEnteTenutoCertBil: string = getDatiBilancioControl(form, 'my_CkEnteTenutoCertBil')?.value;
        const my_CkProvvedeCertBil: string = getDatiBilancioControl(form, 'my_CkProvvedeCertBil')?.value;
        const errors = control.errors ? control.errors : {};
        const showError = isNilOrEmpty(control.value) && my_CkEnteTenutoCertBil === '1' && my_CkProvvedeCertBil === '1';

        addRemoveError(showError, errors, 'my_RagSocRevIncaricataChoice', { status: REQUIRED_STATUS });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_RagSocRevIncaricata: (form: FormGroup) => {
        const control = getDatiBilancioControl(form, 'my_RagSocRevIncaricata');
        const my_CkEnteTenutoCertBil: string = getDatiBilancioControl(form, 'my_CkEnteTenutoCertBil')?.value;
        const my_CkProvvedeCertBil: string = getDatiBilancioControl(form, 'my_CkProvvedeCertBil')?.value;
        const errors = control.errors ? control.errors : {};
        const showError = isNilOrEmpty(control.value) && my_CkEnteTenutoCertBil?.startsWith('1') && my_CkProvvedeCertBil === '1';

        addRemoveError(showError, errors, 'my_RagSocRevIncaricata', { status: REQUIRED_STATUS });

        control.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_EsitoCertificazioneBilancio: (form: FormGroup) => {
        const my_CkEnteTenutoCertBil: string = getDatiBilancioControl(form, 'my_CkEnteTenutoCertBil')?.value;
        datiBilancioCkHelper(form, 'my_EsitoCertificazioneBilancio', 'my_CkProvvedeCertBil', 1, formPaths.bilancio, my_CkEnteTenutoCertBil === '1');
    },

    my_CtrlAllegatoCertificazione: (form: FormGroup) => {
        const my_CtrlAllegatoCertificazioneControl = getDatiBilancioControl(form, 'my_CtrlAllegatoCertificazione');
        const my_CkEnteTenutoCertBil: string = getDatiBilancioControl(form, 'my_CkEnteTenutoCertBil')?.value;
        const my_CkProvvedeCertBil: string = getDatiBilancioControl(form, 'my_CkProvvedeCertBil')?.value;
        const my_ElRelazCertifAllegata = getFormArray(getDatiBilancioGroup(form), 'my_RelazCertifAllegata.my_ElRelazCertifAllegata')?.value;
        const errors = my_CtrlAllegatoCertificazioneControl.errors ? my_CtrlAllegatoCertificazioneControl.errors : {};
        const showError = !my_ElRelazCertifAllegata?.length && my_CkEnteTenutoCertBil === '1' && my_CkProvvedeCertBil === '1';

        addRemoveError(showError, errors, 'my_CtrlAllegatoCertificazione', {
            status: 'Allegare obbligatoriamente la relazione di certificazione',
        });

        my_CtrlAllegatoCertificazioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },

    my_DifformitaRispAutoCertif: (form: FormGroup) => {
        const my_EnableCkEnteTenutoCertBil: string = getDatiBilancioControl(form, 'my_EnableCkEnteTenutoCertBil')?.value;
        datiBilancioCkHelper(form, 'my_DifformitaRispAutoCertif', 'my_CkEnteTenutoCertBil', 0, formPaths.bilancio, toBoolean(my_EnableCkEnteTenutoCertBil));
    },

    my_CtrlAllegatoContrattoCertificazione: (form: FormGroup) => {
        const my_CtrlAllegatoContrattoCertificazioneControl = getDatiBilancioControl(form, 'my_CtrlAllegatoContrattoCertificazione');
        const my_CkEnteTenutoCertBil: string = getDatiBilancioControl(form, 'my_CkEnteTenutoCertBil')?.value;
        const my_CkProvvedeCertBil: string = getDatiBilancioControl(form, 'my_CkProvvedeCertBil')?.value;
        const my_ElContrattoSocietaCertificazione: MyElContrattoSocietaCertificazione[] = getFormArray(
            getDatiBilancioGroup(form),
            'my_ContrattoSocietaCertificazione.my_ElContrattoSocietaCertificazione'
        )?.value;
        const errors = my_CtrlAllegatoContrattoCertificazioneControl.errors ? my_CtrlAllegatoContrattoCertificazioneControl.errors : {};
        const showError = !my_ElContrattoSocietaCertificazione?.length && my_CkEnteTenutoCertBil === '1' && my_CkProvvedeCertBil === '1';

        addRemoveError(showError, errors, 'my_CtrlAllegatoContrattoCertificazione', {
            status: 'Allegare obbligatoriamente il contratto sottoscritto per la relazione di cui sopra',
        });

        my_CtrlAllegatoContrattoCertificazioneControl.setErrors(isEqual(errors, {}) ? null : errors);
    },
};

const my_CkCtrlCertiHelper = (form: FormGroup, targetPath: string) => {
    const targetControl = getDatiBilancioControl(form, targetPath);
    const my_CkCtrlCertifPartecSpA: string = getDatiBilancioControl(form, 'my_CkCtrlCertifPartecSpA')?.value;
    const my_CkCtrlCertifPrestitiEConferim: string = getDatiBilancioControl(form, 'my_CkCtrlCertifPrestitiEConferim')?.value;
    const my_CkCtrlCertifRiserveIndivis: string = getDatiBilancioControl(form, 'my_CkCtrlCertifRiserveIndivis')?.value;
    const my_CkCtrlCertifValore: string = getDatiBilancioControl(form, 'my_CkCtrlCertifValoreProd')?.value;
    const my_CkEnteTenutoCertBil: string = getDatiBilancioControl(form, 'my_CkEnteTenutoCertBil')?.value;
    const showError =
        !toBoolean(my_CkCtrlCertifValore) &&
        !toBoolean(my_CkCtrlCertifPartecSpA) &&
        !toBoolean(my_CkCtrlCertifPrestitiEConferim) &&
        !toBoolean(my_CkCtrlCertifRiserveIndivis) &&
        my_CkEnteTenutoCertBil === '1';
    const errors = targetControl.errors ? targetControl.errors : {};

    addRemoveError(showError, errors, targetPath, {
        status: 'Almeno un motivo obbligatorio',
    });

    targetControl.setErrors(isEqual(errors, {}) ? null : errors);
};
